import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { PageHead, SaveIndicator } from "@/studio/ui";
import { RoomsEditor } from "@/studio/RoomsEditor";
import { PricingPanel } from "@/studio/PricingPanel";
import { AgreementPanel, PaymentsPanel, RecapPanel, SharePanel } from "@/studio/ClientPanels";
import { BeforeAgreement, ConsultationSlots, ConsultationSummary, FurnitureBudget, ProjectActivity } from "@/studio/ProjectPanels";
import { HubHeader, HubTabs, TABS } from "@/studio/v1/Hub";
import { ConceptBoards, ProductBoardPanel } from "@/studio/v1/Boards";
import { OrderingPanel, ServicesPanel } from "@/studio/v1/Ordering";
import { CloseoutPanel, DocumentsPanel, ExceptionsPanel } from "@/studio/v1/Panels";
import { projectPayload, roomToForm } from "@/studio/rooms";

const EMPTY = { name: "", consultation_type: null, rooms: [] };
const toForm = (p) => ({ name: p.name, consultation_type: p.consultation_type, rooms: p.rooms.map(roomToForm) });
const payload = projectPayload;

const ClientPicker = ({ clientId, setClientId }) => {
  const [clients, setClients] = useState([]);
  useEffect(() => { adminApi.get("/studio/clients").then((r) => setClients(r.data)); }, []);
  return (
    <label className="st-field">
      <span className="st-label">Client *</span>
      <select className="st-select" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="project-client-select">
        <option value="">Choose a client…</option>
        {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
      </select>
    </label>
  );
};

const Details = ({ form, change, clientSlot, locked }) => (
  <section className="st-panel" data-testid="project-details">
    <h2 className="st-h2">Project details</h2>
    <div className="st-form">
      {clientSlot}
      <label className="st-field">
        <span className="st-label">Project name *</span>
        <input className="st-input" value={form.name} maxLength={160} onChange={(e) => change((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Brickell Residence" data-testid="project-name-input" />
      </label>
      <div className="st-field">
        <span className="st-label">Consultation *</span>
        {locked ? <p className="st-small" data-testid="project-consult-locked">{form.consultation_type === "remote" ? "Remote" : "Local"} <span className="st-muted">· change it from the Consultation section</span></p> : <div className="st-seg">
          {[["local", "Local"], ["remote", "Remote"]].map(([v, l]) => (
            <button key={v} className={form.consultation_type === v ? "is-on" : ""} onClick={() => change((f) => ({ ...f, consultation_type: v }))} data-testid={`project-consult-${v}`}>{l}</button>
          ))}
        </div>}
      </div>
      <label className="st-field">
        <span className="st-label">Number of rooms</span>
        <input className="st-input" readOnly value={form.rooms.length} style={{ maxWidth: 120 }} data-testid="project-rooms-count-field" />
      </label>
    </div>
  </section>
);

export default function ProjectPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "overview";
  const setTab = (t) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); return n; }, { replace: true });
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [project, setProject] = useState(null);
  const [form, setForm] = useState(null);
  const [clientId, setClientId] = useState(params.get("client") || "");
  const [save, setSave] = useState("idle");
  const pid = useRef(null);
  const formRef = useRef(form);
  const dirty = useRef(false);
  const chain = useRef(Promise.resolve());
  formRef.current = form;

  useEffect(() => { adminApi.get("/studio/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => {
    if (id === "new") {
      pid.current = null; dirty.current = false;
      setProject(null); setForm(EMPTY); setSave("idle");
      return;
    }
    if (pid.current === id) return;
    pid.current = id; dirty.current = false;
    adminApi.get(`/studio/projects/${id}`).then((r) => { setProject(r.data); setForm(toForm(r.data)); });
  }, [id]);

  const persist = async () => {
    if (!pid.current) {
      const f = formRef.current;
      const { data } = await adminApi.post("/studio/projects", { client_id: clientId, name: f.name, consultation_type: f.consultation_type });
      pid.current = data.id;
      setProject(data);
      navigate(`/admin/studio/projects/${data.id}`, { replace: true });
    }
    const { data } = await adminApi.put(`/studio/projects/${pid.current}`, payload(formRef.current));
    setProject(data);
  };

  useEffect(() => {
    if (!dirty.current) return undefined;
    setSave("pending");
    const t = setTimeout(() => {
      setSave("saving");
      chain.current = chain.current.then(persist).then(() => setSave((s) => (s === "saving" ? "saved" : s))).catch(() => setSave("error"));
    }, 600);
    return () => clearTimeout(t);
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (fn) => {
    if (!pid.current && !clientId) return;
    dirty.current = true;
    setForm((f) => fn(f));
  };

  const reload = () => adminApi.get(`/studio/projects/${pid.current}`).then((r) => setProject(r.data));

  if (!meta || !form || (id !== "new" && !project)) return <p className="st-loading">Loading…</p>;
  const isNew = !pid.current;
  const snap = project?.pricing_snapshot;

  return (
    <div className="st-page" data-testid="project-page">
      <PageHead eyebrow={project?.client ? <Link to={`/admin/studio/clients/${project.client.id}`} data-testid="project-client-link">{project.client.full_name}</Link> : "New project"}
        title={form.name || "Untitled project"}>
        <SaveIndicator state={save} />
      </PageHead>
      {isNew && !clientId && <p className="st-note" style={{ marginBottom: 24 }}>Choose a client to begin. The draft saves automatically as you type.</p>}
      {project && <HubHeader project={project} onDone={reload} setTab={setTab} />}
      {project && <HubTabs tab={tab} setTab={setTab} />}
      <div className="st-grid2" data-testid={`hub-panel-${tab}`}>
        {(isNew || tab === "overview") && <>
          <div>
            <Details form={form} change={change} locked={!!project?.consultation_type} clientSlot={isNew ? <ClientPicker clientId={clientId} setClientId={setClientId} /> : null} />
            <RoomsEditor rooms={form.rooms} change={change} meta={meta} snap={snap} previewBudgets={project?.furniture_budgets}
              serverRooms={Object.fromEntries((project?.rooms || []).map((r) => [r.id, r]))} />
          </div>
          <div>
            {project ? <><BeforeAgreement project={project} /><ExceptionsPanel project={project} onProject={setProject} /><CloseoutPanel project={project} onProject={setProject} /></>
              : <section className="st-panel"><p className="st-muted">Pricing appears once the draft is saved.</p></section>}
          </div>
        </>}
        {project && tab === "consultation" && <><div><ConsultationSlots project={project} onProject={(d) => { setProject(d); setForm((f) => ({ ...f, consultation_type: d.consultation_type })); }} /></div><div><ConsultationSummary project={project} /></div></>}
        {project && tab === "pricing" && <><div><PricingPanel project={project} onProject={setProject} /><ServicesPanel project={project} onChange={reload} /></div><div><FurnitureBudget project={project} /></div></>}
        {project && tab === "proposal" && <><div><RecapPanel project={project} /></div><div><AgreementPanel project={project} onChange={reload} /><SharePanel project={project} /></div></>}
        {project && tab === "payments" && <><div><PaymentsPanel project={project} onChange={reload} /></div><div><SharePanel project={project} /></div></>}
        {project && tab === "boards" && <div className="st-span2"><ConceptBoards project={project} onChange={reload} /></div>}
        {project && tab === "products" && <div className="st-span2"><ProductBoardPanel project={project} onChange={reload} /></div>}
        {project && tab === "ordering" && <><div><OrderingPanel project={project} onChange={reload} /></div><div><ServicesPanel project={project} onChange={reload} /></div></>}
        {project && tab === "documents" && <><div><DocumentsPanel project={project} /></div><div><ProjectActivity projectId={project.id} version={`${project.status}-${project.updated_at}`} /></div></>}
      </div>
    </div>
  );
}
